/**
 * Cross-tab "the login state changed" signal. Carries no payload that is trusted: a receiving tab
 * re-asks the server (CheckSessionStatus) instead of believing the message.
 */
const CHANNEL_NAME = 'cablepro-auth';
const STORAGE_KEY = 'cablepro-auth-changed';
// BroadcastChannel delivers to every channel object in the sender's own page too (only the
// sending object is skipped), so the originating tab must recognise and ignore its own message.
// Otherwise the login tab re-checks mid-redirect and its reload() cancels the pending navigation.
const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export class AuthChangeSignal {
    private static channel: BroadcastChannel | null = null;

    static notify(): void {
        try {
            if (typeof BroadcastChannel !== 'undefined') {
                AuthChangeSignal.getChannel()?.postMessage(TAB_ID);
            } else {
                // 'storage' only fires in other tabs, which is exactly what is wanted.
                localStorage.setItem(STORAGE_KEY, String(Date.now()));
            }
        } catch { /* best effort - the slow safety-net poll still catches it */ }
    }

    /** Returns an unsubscribe function. */
    static listen(handler: () => void): () => void {
        try {
            if (typeof BroadcastChannel !== 'undefined') {
                const channel = new BroadcastChannel(CHANNEL_NAME);
                channel.onmessage = (e: MessageEvent) => {
                    if (e.data !== TAB_ID) {
                        handler();
                    }
                };
                return () => channel.close();
            }
        } catch { /* fall through to storage */ }

        const onStorage = (e: StorageEvent) => {
            if (e.key === STORAGE_KEY) {
                handler();
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }

    private static getChannel(): BroadcastChannel | null {
        if (!AuthChangeSignal.channel) {
            AuthChangeSignal.channel = new BroadcastChannel(CHANNEL_NAME);
        }
        return AuthChangeSignal.channel;
    }
}
